import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { openSupportEmail } from '../../support/contact-support';
import { GetHelpStateService } from '../get-help-state.service';
import { GET_HELP_QUICK_QUESTIONS, GetHelpDraft, GetHelpSentKind, GetHelpView } from '../get-help.models';

interface QuickQuestion {
    label: string;
}

const NOT_PROVIDED = 'Not provided';

/**
 * The "How can we help?" callout content of My Day's help banner (design: Trovve.dc.html `hlp` — bHome / bAsk / bSetup /
 * bSent). The banner owns the floating frame (position, scrim, arrow); this owns the views.
 *
 * Nothing is sent by Trovve. Both actions open a prefilled draft to help@trovve.com in the user's own mail app (Outlook
 * on the web inside Teams), which the user sends from their own address:
 *  - "Ask a question": pick a suggested question (fills the box, never sends) or type one, then Send question.
 *    Subject "New lead question — [Company] — [Name]"; body "Question:", the question, then Name / Email / Company.
 *  - "Get setup help": subject "Setup help request — [Company] — [Name]" with the same details.
 * Name, email and company come from the account; a missing name or company reads "Not provided".
 *
 * The view and the typed question are saved in GetHelpStateService as they change, so leaving My Day (or refreshing)
 * during the session and coming back restores them.
 */
@Component({
    selector: 'vf-get-help-panel',
    templateUrl: './get-help-panel.component.html',
    styleUrls: ['./get-help-panel.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GetHelpPanelComponent implements OnInit {
    /** The user closed the callout (✕, Esc, or Done on the setup-help confirmation). */
    @Output() closed = new EventEmitter<void>();
    /** The question or setup-help draft was opened in the user's mail app. */
    @Output() sent = new EventEmitter<GetHelpSentKind>();
    /** The current view, so the banner can label its dialog. */
    @Output() viewChange = new EventEmitter<GetHelpView>();

    @ViewChild('questionBox') set questionBox(el: ElementRef<HTMLTextAreaElement> | undefined) {
        // Focus the textarea when the Ask view opens (design `qRef`), once per opening.
        if (el && !this.questionFocused) {
            this.questionFocused = true;
            setTimeout(() => el.nativeElement.focus(), 40);
        }
        if (!el) {
            this.questionFocused = false;
        }
    }

    view: GetHelpView = 'home';
    canGoBack = false;
    question = '';
    showAllQuestions = false;
    quickQuestions: QuickQuestion[] = [];
    sendDisabled = true;
    sentKind: GetHelpSentKind = 'question';
    sentText = '';

    private questionFocused = false;

    constructor(
        private state: GetHelpStateService,
        private appSession: AppSessionService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        const draft: GetHelpDraft | null = this.state.getDraft();
        if (draft && draft.view !== 'sent') {
            this.view = draft.view;
            this.question = draft.question;
            this.canGoBack = this.view === 'ask' || this.view === 'setup';
        }
        this.refreshQuickQuestions();
        this.refreshSendState();
    }

    @HostListener('keydown.escape')
    onEscape(): void {
        this.close();
    }

    close(): void {
        this.closed.emit();
    }

    goHome(): void {
        this.showAllQuestions = false;
        this.refreshQuickQuestions();
        this.setView('home');
    }

    goAsk(): void {
        this.setView('ask');
    }

    goSetup(): void {
        this.setView('setup');
    }

    onQuestionInput(value: string): void {
        this.question = value;
        this.refreshSendState();
        this.saveDraft();
    }

    /** Fills the box with a suggested question; the user still has to press Send question. */
    pickQuestion(item: QuickQuestion): void {
        this.onQuestionInput(item.label);
    }

    showMoreQuestions(): void {
        this.showAllQuestions = true;
        this.refreshQuickQuestions();
    }

    sendQuestion(): void {
        // Mail clients expect CRLF line breaks; the textarea gives bare LF.
        const question = this.question.trim().replace(/\r?\n/g, '\r\n');
        if (!question) {
            return;
        }
        this.openDraft('New lead question', 'Question:\r\n' + question + '\r\n\r\n');
        this.question = '';
        this.refreshSendState();
        this.showSent('question');
    }

    requestSetupHelp(): void {
        this.openDraft('Setup help request', 'I would like help getting Trovve set up.\r\n\r\n');
        this.showSent('setup');
    }

    trackByQuestion(_index: number, item: QuickQuestion): string {
        return item.label;
    }

    /** Subject "<kind> — [Company name] — [Person's name]"; the body ends with Name / Email / Company from the account. */
    private openDraft(kind: string, opening: string): void {
        const user = this.appSession.user;
        const name = (((user && user.name) || '') + ' ' + ((user && user.surname) || '')).trim() || NOT_PROVIDED;
        const company = (this.appSession.tenant && this.appSession.tenant.name) || NOT_PROVIDED;
        const email = (user && user.emailAddress) || NOT_PROVIDED;
        openSupportEmail({
            subject: kind + ' — ' + company + ' — ' + name,
            body: opening + 'Name: ' + name + '\r\nEmail: ' + email + '\r\nCompany: ' + company + '\r\n',
        });
    }

    private showSent(kind: GetHelpSentKind): void {
        this.sentKind = kind;
        this.sentText =
            kind === 'setup'
                ? 'Send it from your email app and a real person from Trovve will follow up, usually within one business day.'
                : 'Send it from your email app and we’ll reply by email, usually within one business day.';
        this.setView('sent');
        this.sent.emit(kind);
    }

    private setView(view: GetHelpView): void {
        this.view = view;
        this.canGoBack = view === 'ask' || view === 'setup';
        this.saveDraft();
        this.viewChange.emit(view);
        this.cdr.markForCheck();
    }

    private saveDraft(): void {
        this.state.saveDraft({ calloutOpen: true, view: this.view, question: this.question });
    }

    private refreshQuickQuestions(): void {
        const source = this.showAllQuestions ? GET_HELP_QUICK_QUESTIONS : GET_HELP_QUICK_QUESTIONS.slice(0, 3);
        this.quickQuestions = source.map((label: string) => ({ label }));
        this.cdr.markForCheck();
    }

    private refreshSendState(): void {
        this.sendDisabled = !this.question.trim();
        this.cdr.markForCheck();
    }
}
